import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazy-load';
import get from 'lodash.get';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';

import { RenderIn, device } from '../../utils/RenderIn';
import {
    trackInitialDisplay,
    trackArrowClick,
    trackItemClick,
    trackRecentItems,
} from 'dibs-recent-history/exports/recentHistoryTracking';
import { HpSharedCarouselTracking } from '../HpSharedCarouselTracking/HpSharedCarouselTracking';
import styles from './HpRespRecentlyViewedItemsCarousel.scss';

const OFFSET_IMAGE_VERTICAL = 600;
const OFFSET_IMAGE_HORIZONTAL = 300;

class HpRespRecentlyViewedItemsCarousel extends Component {
    handleItemClick({ item, index }) {
        trackItemClick();
        trackRecentItems({
            type: 'productClick',
            items: [item],
            startIndex: index + 1, // Tracking needs non zero index position.
            variant: 'homepage',
        });
    }

    trackImpressions({ visibleItems, index }) {
        trackRecentItems({
            type: 'productImpression',
            items: visibleItems,
            startIndex: index + 1, // Tracking needs non zero index position.
            variant: 'homepage',
        });
    }

    onInitialDisplay = () => {
        const itemsReturned = this.props.items.length;
        trackInitialDisplay({ itemsReturned });
    };

    getCarouselItems() {
        const { items } = this.props;

        return items.map((item, index) => (
            <a
                key={`recent-${item.itemId}`}
                className={styles.itemLink}
                href={item.itemPdpUrl}
                onClick={() => this.handleItemClick({ item, index })}
            >
                <LazyLoad
                    offsetHorizontal={OFFSET_IMAGE_HORIZONTAL}
                    offsetVertical={OFFSET_IMAGE_VERTICAL}
                >
                    <img
                        className={styles.image}
                        src={get(item.itemImageUrl, 'src') || item.itemImageUrl}
                        alt={item.itemTitle}
                    />
                </LazyLoad>
            </a>
        ));
    }

    render() {
        const { itemsPerPage, items } = this.props;
        const itemsNode = this.getCarouselItems();

        return (
            <Fragment>
                <RenderIn deviceTypes={[device.DESKTOP]}>
                    <HpSharedCarouselTracking
                        items={items}
                        itemsPerPage={itemsPerPage}
                        onItemsImpression={this.trackImpressions}
                        onInitialDisplay={this.onInitialDisplay}
                    >
                        {({ handleIndexChange }) => (
                            <Carousel
                                totalItems={itemsNode.length}
                                itemsPerPage={itemsPerPage}
                                step={itemsPerPage}
                                renderItem={({ index }) => itemsNode[index]}
                                onIndexChange={props => {
                                    handleIndexChange(props);
                                    /*
                                     * Needed for lazy images to load.
                                     */
                                    this.forceUpdate();
                                }}
                                onLeftArrowClick={() => trackArrowClick({ direction: 'left' })}
                                onRightArrowClick={() => trackArrowClick({ direction: 'right' })}
                            />
                        )}
                    </HpSharedCarouselTracking>
                </RenderIn>

                <RenderIn deviceTypes={[device.MOBILE, device.TABLET]}>
                    <HpSharedCarouselTracking
                        items={items}
                        itemsPerPage={itemsPerPage}
                        onItemsImpression={this.trackImpressions}
                        onInitialDisplay={this.onInitialDisplay}
                    >
                        {({ handlePageChange }) => (
                            <Swiper
                                itemsPerPage={itemsPerPage}
                                onPageChange={page => {
                                    handlePageChange(page);
                                    /*
                                     * Needed for lazy images to load.
                                     */
                                    this.forceUpdate();
                                }}
                            >
                                {itemsNode}
                            </Swiper>
                        )}
                    </HpSharedCarouselTracking>
                </RenderIn>
            </Fragment>
        );
    }
}

HpRespRecentlyViewedItemsCarousel.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            contemporaryTrackingString: PropTypes.string,
            itemTitle: PropTypes.string.isRequired,
            itemId: PropTypes.string.isRequired,
            itemImageUrl: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
            itemPdpUrl: PropTypes.string.isRequired,
            dealerPk: PropTypes.string.isRequired,
            category: PropTypes.string.isRequired,
        })
    ).isRequired,
    itemsPerPage: PropTypes.number.isRequired,
};

export { HpRespRecentlyViewedItemsCarousel };
